if (!customElements.get('rug-ar')) {
  class RugAr extends HTMLElement {
    constructor() {
      super();
      this.handleTrigger = this.handleTrigger.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.closePreview = this.closePreview.bind(this);
    }

    connectedCallback() {
      try {
        this.models = JSON.parse(this.querySelector('[data-rug-ar-models]')?.textContent || '{}');
      }
      catch (error) {
        this.models = {};
      }

      this.trigger = this.querySelector('[data-rug-ar-trigger]');
      this.dialog = this.querySelector('[data-rug-ar-dialog]');
      this.closeButton = this.querySelector('[data-rug-ar-close]');
      this.viewer = this.querySelector('[data-rug-ar-viewer]');
      this.status = this.querySelector('[data-rug-ar-status]');
      this.sizeLabel = this.querySelector('[data-rug-ar-size]');
      this.productForm = document.getElementById(this.dataset.productFormId || '');
      this.currentVariantId = String(this.dataset.selectedVariant || '');

      this.trigger?.addEventListener('click', this.handleTrigger);
      this.closeButton?.addEventListener('click', this.closePreview);
      this.dialog?.addEventListener('click', this.handleDialogClick);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      this.productForm?.addEventListener('variant:change', this.handleVariantChange);
      this.updateVariant(this.currentVariantId);

      const url = new URL(window.location.href);
      if (url.searchParams.get('rug_ar_fallback') === '1') {
        const fallbackVariant = url.searchParams.get('rug_ar_variant') || this.currentVariantId;
        this.updateVariant(fallbackVariant);
        url.searchParams.delete('rug_ar_fallback');
        url.searchParams.delete('rug_ar_variant');
        window.history.replaceState({}, '', url);
        this.openPreview();
      }
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.handleTrigger);
      this.closeButton?.removeEventListener('click', this.closePreview);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      this.productForm?.removeEventListener('variant:change', this.handleVariantChange);
    }

    get currentModel() {
      return this.models?.[this.currentVariantId];
    }

    handleVariantChange(event) {
      const variantId = event.detail?.variant?.id;
      if (variantId) this.updateVariant(String(variantId));
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closePreview();
    }

    handleDialogClose() {
      if (this.viewer) this.viewer.hidden = true;
    }

    updateVariant(variantId) {
      const model = this.models?.[String(variantId)];
      if (!model) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.currentVariantId = String(variantId);
      this.trigger.href = `${model.usdz}#allowsContentScaling=0`;
      this.trigger.setAttribute(
        'aria-label',
        `View ${this.dataset.productTitle} in your room at ${model.title} size`
      );
      if (this.sizeLabel) this.sizeLabel.textContent = model.title;

      if (this.viewer && !this.viewer.hidden) {
        this.viewer.src = model.glb;
        this.viewer.setAttribute('ios-src', `${model.usdz}#allowsContentScaling=0`);
      }
    }

    supportsQuickLook() {
      const link = document.createElement('a');
      return Boolean(link.relList?.supports && link.relList.supports('ar'));
    }

    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    }

    handleTrigger(event) {
      const model = this.currentModel;
      if (!model) {
        event.preventDefault();
        return;
      }

      // A real rel="ar" link is retained for iOS so Quick Look launches from the
      // original user gesture. No model-viewer bundle is downloaded in this path.
      if (this.isIOS() && this.supportsQuickLook()) return;

      event.preventDefault();

      if (this.isAndroid()) {
        const fallback = new URL(window.location.href);
        fallback.searchParams.set('variant', this.currentVariantId);
        fallback.searchParams.set('rug_ar_fallback', '1');
        fallback.searchParams.set('rug_ar_variant', this.currentVariantId);

        const params = new URLSearchParams({
          file: model.glb,
          mode: 'ar_preferred',
          resizable: 'false',
          title: `${this.dataset.productTitle} — ${model.title}`,
        });
        window.location.href = `intent://arvr.google.com/scene-viewer/1.0?${params.toString()}`
          + '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;'
          + 'action=android.intent.action.VIEW;'
          + `S.browser_fallback_url=${encodeURIComponent(fallback.toString())};end;`;
        return;
      }

      this.openPreview();
    }

    async loadModelViewer() {
      if (customElements.get('model-viewer')) return;

      if (!window.rugArModelViewerPromise) {
        window.rugArModelViewerPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.type = 'module';
          script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.3.1/model-viewer.min.js';
          script.crossOrigin = 'anonymous';
          script.dataset.rugArModelViewer = '';
          script.addEventListener('load', async () => {
            await customElements.whenDefined('model-viewer');
            resolve();
          }, { once: true });
          script.addEventListener('error', () => {
            script.remove();
            window.rugArModelViewerPromise = null;
            reject(new Error('3D preview is unavailable.'));
          }, { once: true });
          document.head.appendChild(script);
        });
      }

      await window.rugArModelViewerPromise;
    }

    async openPreview() {
      const model = this.currentModel;
      if (!model || !this.dialog) return;

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      }
      else {
        this.dialog.setAttribute('open', '');
      }

      this.status.hidden = false;
      this.status.textContent = 'Loading 3D preview…';

      try {
        await this.loadModelViewer();
        const modelAlreadyLoaded = this.viewer.loaded
          && this.viewer.getAttribute('src') === model.glb;

        const clearModelListeners = () => {
          this.viewer.removeEventListener('load', handleModelLoad);
          this.viewer.removeEventListener('error', handleModelError);
        };
        const handleModelLoad = () => {
          clearModelListeners();
          this.status.hidden = true;
        };
        const handleModelError = () => {
          clearModelListeners();
          this.status.hidden = false;
          this.status.textContent = 'This device could not load the 3D preview.';
        };

        this.viewer.addEventListener('load', handleModelLoad);
        this.viewer.addEventListener('error', handleModelError);
        this.viewer.setAttribute('src', model.glb);
        this.viewer.setAttribute('ios-src', `${model.usdz}#allowsContentScaling=0`);
        this.viewer.hidden = false;

        if (modelAlreadyLoaded) handleModelLoad();
      }
      catch (error) {
        this.status.hidden = false;
        this.status.textContent = 'This device could not load the 3D preview.';
      }
    }

    closePreview() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      if (this.viewer) this.viewer.hidden = true;
    }
  }

  customElements.define('rug-ar', RugAr);
}
